import { ClipboardList } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';
import { COMPANY_DOC_SLUGS } from '../constants';
import { X, Search, CheckCircle, XCircle, Clock, FileText, Eye, ExternalLink, UserPlus, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Types ────────────────────────────────────────────────────────────────────

interface CandidateDoc { code: string; label: string; required: boolean; applicableForFreshers: boolean; status: string; fileUrl: string; submittedAt?: string; verifiedBy?: any; rejectionReason?: string }
interface CompanyDoc { code: string; label: string; status: string; fileUrl: string; generatedAt?: string; acknowledgedAt?: string }
interface Candidate {
  _id: string; candidateId: string; source: string;
  firstName: string; lastName: string; email: string; phone: string; alternatePhone: string;
  dateOfBirth: string; gender: string; maritalStatus: string; bloodGroup: string; disability: string;
  aadharNumber: string; panNumber: string; uanNumber: string; esicNumber: string; drivingLicense: string;
  currentAddress: { street: string; city: string; state: string; pincode: string };
  permanentAddress: { street: string; city: string; state: string; pincode: string };
  bankName: string; accountNumber: string; ifscCode: string; branchName: string;
  family: { name: string; relation: string; contact: string }[];
  education: { schoolName: string; degree: string; fieldOfStudy: string; completionDate: string }[];
  experience: { company: string; occupation: string; duration: string; reasonForLeaving: string }[];
  designation: string; department: string; dateOfJoining: string; location: string;
  highestQualification: string; currentSalary: number; reportingTo: string;
  employmentType: string; isFresher: boolean; skillSet: string[];
  onboardingStatus: string; completionPercent: number;
  candidateDocuments: CandidateDoc[]; companyDocuments: CompanyDoc[];
  eJoiningToken: string; addedBy?: any; addedAt: string; modifiedBy?: any; modifiedAt?: string;
  /** Populated when a Zynx User account has been provisioned (see convert-to-employee). Null until then. */
  employeeUserId: { _id: string; username: string; active: number; display_name: string } | null;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

function useCandidates(filters: { page?: number; search?: string; status?: string; source?: string }) {
  return useQuery({
    queryKey: ['onboarding-candidates', filters],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (filters.page) p.append('page', String(filters.page));
      if (filters.search) p.append('search', filters.search);
      if (filters.status) p.append('status', filters.status);
      if (filters.source) p.append('source', filters.source);
      const { data } = await api.get(`/api/hr-onboarding-candidates/candidates?${p}`);
      return data as { data: Candidate[]; pagination: { total: number; page: number; totalPages: number } };
    },
  });
}

function useCandidateDetail(id: string | null) {
  return useQuery({
    queryKey: ['onboarding-candidates', 'detail', id],
    queryFn: async () => { const { data } = await api.get(`/api/hr-onboarding-candidates/candidates/${id}`); return data as Candidate; },
    enabled: !!id,
  });
}

function useAddDirectHire() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => { const { data } = await api.post('/api/hr-onboarding-candidates/candidates', payload); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

function useVerifyDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, code }: { id: string; code: string }) => { const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/verify-doc`, { code }); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

function useRejectDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, code, reason }: { id: string; code: string; reason?: string }) => { const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/reject-doc`, { code, reason }); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

/** Provisions the real Zynx User account for this candidate (inactive until activated). */
function useConvertToEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/convert-to-employee`); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

/** Flips the provisioned account active and returns a login-credentials emailDraft for HR to review/send. */
function useActivateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/activate-account`); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

// ── Status helpers ───────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  pending_info: 'bg-gray-100 text-gray-700', info_submitted: 'bg-info/10 text-info-text',
  docs_pending: 'bg-warning/10 text-warning-text', docs_submitted: 'bg-indigo-100 text-indigo-800',
  verification: 'bg-purple-100 text-purple-800', completed: 'bg-success/10 text-success-text',
};

const sourceLabels: Record<string, string> = { recruitment: 'Recruitment', website: 'Website', linkedin: 'LinkedIn', naukri: 'Naukri', direct_hire: 'Direct Hire' };

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function HrOnboardingPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useCandidates({ page, search: search || undefined, status: statusFilter || undefined, source: sourceFilter || undefined });
  const candidates = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Left: Candidate List */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <PageHeader icon={ClipboardList} eyebrow="People" title="Onboarding — Candidates" description={<>Click a row to open detail drawer.</>} actions={<><Button size="sm" onClick={() => setShowAdd(true)}><UserPlus className="h-4 w-4 mr-1" />Add Direct Hire</Button></>} />
          {/* Filters */}
          <div className="flex gap-2 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" placeholder="Search by name, email, ID..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="pending_info">Pending Info</option>
              <option value="info_submitted">Info Submitted</option>
              <option value="docs_submitted">Docs Submitted</option>
              <option value="verification">Verification</option>
              <option value="completed">Completed</option>
            </select>
            <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900" value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }}>
              <option value="">All Sources</option>
              <option value="recruitment">Recruitment</option>
              <option value="website">Website</option>
              <option value="linkedin">LinkedIn</option>
              <option value="naukri">Naukri</option>
              <option value="direct_hire">Direct Hire</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {isLoading ? <div className="p-6"><Skeleton className="h-[400px]" /></div> : (
          <div className="flex-1 overflow-auto px-6">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3">ID</th>
                  <th className="px-3 py-3">First Name</th>
                  <th className="px-3 py-3">Last Name</th>
                  <th className="px-3 py-3">Email</th>
                  <th className="px-3 py-3">Department</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Source</th>
                  <th className="px-3 py-3 text-right">Completion</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {candidates.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No candidates found.</td></tr>}
                {candidates.map(c => (
                  <tr key={c._id} onClick={() => navigate(`/hr-onboarding/${c._id}`)} className="cursor-pointer hover:bg-primary/5 transition">
                    <td className="px-3 py-3 font-mono text-xs">{c.candidateId}</td>
                    <td className="px-3 py-3 font-medium">{c.firstName || '-'}</td>
                    <td className="px-3 py-3">{c.lastName || '-'}</td>
                    <td className="px-3 py-3 text-muted-foreground truncate max-w-[160px]">{c.email || '-'}</td>
                    <td className="px-3 py-3 capitalize">{c.department || '-'}</td>
                    <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${statusColors[c.onboardingStatus] || 'bg-gray-100'}`}>{c.onboardingStatus?.replace(/_/g, ' ')}</span></td>
                    <td className="px-3 py-3 text-xs">{sourceLabels[c.source] || c.source}</td>
                    <td className="px-3 py-3 text-right"><div className="flex items-center justify-end gap-2"><div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${c.completionPercent}%` }} /></div><span className="text-xs">{c.completionPercent}%</span></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-3 border-t flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Record Count: <strong className="text-primary">{pagination.total}</strong></span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-30">&lt;</button>
              <span className="px-3 py-1">{page} / {pagination.totalPages}</span>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-30">&gt;</button>
            </div>
          </div>
        )}
      </div>

      {/* Right: navigate to detail page */}
      {/* Detail is now a full-page route, not a modal */}

      {/* Add Direct Hire Modal */}
      <AddDirectHireModal open={showAdd} onClose={() => setShowAdd(false)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CANDIDATE DETAIL DRAWER
// ═══════════════════════════════════════════════════════════════════════════════

export function CandidateDetailModal({ candidateId, onClose }: { candidateId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: c, isLoading } = useCandidateDetail(candidateId);
  const verifyMut = useVerifyDoc();
  const rejectMut = useRejectDoc();
  const convertMut = useConvertToEmployee();
  const activateMut = useActivateAccount();
  const [emailDraft, setEmailDraft] = useState<{ to: string; subject: string; html: string } | null>(null);

  if (isLoading || !c) return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{backgroundColor:'rgba(0,0,0,0.6)'}}>
      <div className="bg-white rounded-xl w-[90vw] max-w-5xl h-[85vh] p-6"><Skeleton className="h-full" /></div>
    </div>
  );

  const docIcon = (s: string) => {
    if (s === 'verified') return <CheckCircle className="h-4 w-4 text-success-text" />;
    if (s === 'rejected') return <XCircle className="h-4 w-4 text-danger-text" />;
    if (s === 'submitted') return <FileText className="h-4 w-4 text-info-text" />;
    if (s === 'not_applicable') return <span className="text-[10px] text-gray-400">N/A</span>;
    return <Clock className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{backgroundColor:'rgba(0,0,0,0.6)'}} onClick={onClose}>
      <div className="bg-white rounded-xl w-[92vw] max-w-6xl h-[88vh] flex flex-col overflow-hidden shadow-2xl border" onClick={e => e.stopPropagation()}>
      {/* Modal Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50 rounded-t-xl shrink-0">
        <div>
          <h2 className="font-semibold text-lg text-gray-900">{c.firstName} {c.lastName}</h2>
          <p className="text-xs text-gray-500">{c.candidateId} — {c.designation || 'Position TBD'} — <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[c.onboardingStatus]}`}>{c.onboardingStatus?.replace(/_/g, ' ')}</span></p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition"><X className="h-5 w-5 text-gray-600" /></button>
      </div>

      {/* Scrollable content - 2 column layout for wider modal */}
      <div className="flex-1 overflow-auto p-6 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 text-sm">

        {/* Section: Candidate Details */}
        <Section title="Candidate Details">
          <Grid>
            <KV label="Candidate ID" value={c.candidateId} />
            <KV label="First Name" value={c.firstName} />
            <KV label="Last Name" value={c.lastName} />
            <KV label="Email ID" value={c.email} />
            <KV label="Phone" value={c.phone} />
            <KV label="Alternate Phone" value={c.alternatePhone} />
            <KV label="Date of Birth" value={c.dateOfBirth?.slice(0, 10)} />
            <KV label="Gender" value={c.gender} />
            <KV label="Marital Status" value={c.maritalStatus} />
            <KV label="Blood Group" value={c.bloodGroup} />
            <KV label="Disability" value={c.disability} />
            <KV label="UAN Number" value={c.uanNumber} />
            <KV label="Aadhar Number" value={c.aadharNumber} />
            <KV label="PAN Number" value={c.panNumber} />
          </Grid>
        </Section>

        {/* Section: Address */}
        <Section title="Address Details">
          <Grid>
            <KV label="Current Address" value={fmtAddr(c.currentAddress)} span />
            <KV label="Permanent Address" value={fmtAddr(c.permanentAddress)} span />
          </Grid>
        </Section>

        {/* Section: Bank */}
        <Section title="Bank Details">
          <Grid>
            <KV label="Bank Name" value={c.bankName} />
            <KV label="Account Number" value={c.accountNumber} />
            <KV label="IFSC Code" value={c.ifscCode} />
            <KV label="Branch" value={c.branchName} />
          </Grid>
        </Section>

        {/* Section: Family */}
        <Section title="Family Details">
          {c.family?.length ? (
            <table className="w-full text-xs border-collapse"><thead><tr className="bg-muted/50"><th className="px-2 py-1.5 text-left">S.No</th><th className="px-2 py-1.5 text-left">Name</th><th className="px-2 py-1.5 text-left">Relation</th><th className="px-2 py-1.5 text-left">Contact</th></tr></thead>
            <tbody>{c.family.map((f, i) => <tr key={i} className="border-t"><td className="px-2 py-1.5">{i+1}</td><td className="px-2 py-1.5">{f.name}</td><td className="px-2 py-1.5 capitalize">{f.relation}</td><td className="px-2 py-1.5">{f.contact}</td></tr>)}</tbody></table>
          ) : <p className="text-muted-foreground text-xs">No family details.</p>}
        </Section>

        {/* Section: Professional */}
        <Section title="Professional Details">
          <Grid>
            <KV label="Designation" value={c.designation} />
            <KV label="Department" value={c.department} />
            <KV label="Date of Joining" value={c.dateOfJoining?.slice(0, 10)} />
            <KV label="Location" value={c.location} />
            <KV label="Source of Hire" value={sourceLabels[c.source] || c.source} />
            <KV label="Skill Set" value={c.skillSet?.join(', ')} />
            <KV label="Highest Qualification" value={c.highestQualification} />
            <KV label="Current Salary" value={c.currentSalary ? `₹${c.currentSalary.toLocaleString('en-IN')}` : '-'} />
            <KV label="Reporting To" value={c.reportingTo} />
            <KV label="Employment Type" value={c.employmentType} />
            <KV label="Fresher" value={c.isFresher ? 'Yes' : 'No'} />
          </Grid>
        </Section>

        {/* Section: Education */}
        <Section title="Education">
          {c.education?.length ? (
            <table className="w-full text-xs border-collapse"><thead><tr className="bg-muted/50"><th className="px-2 py-1.5 text-left">School Name</th><th className="px-2 py-1.5 text-left">Degree</th><th className="px-2 py-1.5 text-left">Field</th><th className="px-2 py-1.5 text-left">Completion</th></tr></thead>
            <tbody>{c.education.map((e, i) => <tr key={i} className="border-t"><td className="px-2 py-1.5">{e.schoolName||'-'}</td><td className="px-2 py-1.5">{e.degree||'-'}</td><td className="px-2 py-1.5">{e.fieldOfStudy||'-'}</td><td className="px-2 py-1.5">{e.completionDate?.slice(0,10)||'-'}</td></tr>)}</tbody></table>
          ) : <p className="text-muted-foreground text-xs">No education records.</p>}
        </Section>

        {/* Section: Experience */}
        <Section title="Work Experience">
          {c.isFresher ? <p className="text-muted-foreground text-xs italic">Fresher — no previous experience.</p> : c.experience?.length ? (
            <table className="w-full text-xs border-collapse"><thead><tr className="bg-muted/50"><th className="px-2 py-1.5 text-left">Company</th><th className="px-2 py-1.5 text-left">Role</th><th className="px-2 py-1.5 text-left">Duration</th><th className="px-2 py-1.5 text-left">Reason</th></tr></thead>
            <tbody>{c.experience.map((e, i) => <tr key={i} className="border-t"><td className="px-2 py-1.5">{e.company||'-'}</td><td className="px-2 py-1.5">{e.occupation||'-'}</td><td className="px-2 py-1.5">{e.duration||'-'}</td><td className="px-2 py-1.5">{e.reasonForLeaving||'-'}</td></tr>)}</tbody></table>
          ) : <p className="text-muted-foreground text-xs">No experience records.</p>}
        </Section>

        {/* Section: Company Documents — full width. Every document with a
            template (see COMPANY_DOC_SLUGS) opens the same viewer/editor for
            ANY status: not-yet-generated shows a live preview, draft/issued
            are both editable there, and Approve & Issue / Re-issue happens
            in that page. Nothing here calls a generate/issue endpoint
            directly anymore — the viewer owns that whole flow so a document
            is never a one-shot action instead of an editable draft (2026-08-14). */}
        <div className="lg:col-span-2">
        <Section title="Company Documents">
          <div className="space-y-1.5">
            {c.companyDocuments?.map(doc => {
              const issued = doc.status === 'issued' || doc.status === 'acknowledged';
              const draft = doc.status === 'draft';
              return (
                <div key={doc.code} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    {issued ? <CheckCircle className="h-3.5 w-3.5 text-success-text" /> : draft ? <FileText className="h-3.5 w-3.5 text-warning-text" /> : <Clock className="h-3.5 w-3.5 text-gray-400" />}
                    <span className="text-xs">{doc.label}</span>
                    {issued && <span className="text-[10px] text-success-text font-medium">Issued</span>}
                    {draft && <span className="text-[10px] text-warning-text font-medium">Draft</span>}
                  </div>
                  <div className="flex gap-1">
                    {COMPANY_DOC_SLUGS[doc.code] ? (
                      <button onClick={() => {
                        onClose();
                        navigate(`/hr-onboarding/${c._id}/documents/${doc.code}`);
                      }} className="text-[10px] px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1">
                        <Eye className="h-3 w-3" />{doc.status === 'not_generated' ? 'Open' : 'View / Edit'}
                      </button>
                    ) : (
                      <span className="text-[10px] text-gray-400">No template</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
        </div>

        {/* Section: Candidate Documents (Uploaded) — full width */}
        <div className="lg:col-span-2">
        <Section title="Candidate Documents">
          <div className="space-y-1.5">
            {c.candidateDocuments?.map(doc => (
              <div key={doc.code} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  {docIcon(doc.status)}
                  <div>
                    <span className="text-xs">{doc.label}</span>
                    {doc.required && doc.status !== 'not_applicable' && <span className="text-danger-text text-[10px] ml-1">*</span>}
                    {doc.status === 'rejected' && <span className="text-[10px] text-danger-text ml-2">({doc.rejectionReason})</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  {doc.status === 'submitted' && <>
                    <button onClick={() => verifyMut.mutateAsync({ id: c._id, code: doc.code }).then(() => toast.success('Verified.'))} className="text-[10px] px-2 py-1 bg-success/10 text-success-text rounded hover:bg-success/10">Verify</button>
                    <button onClick={() => { const r = prompt('Rejection reason:'); if (r) rejectMut.mutateAsync({ id: c._id, code: doc.code, reason: r }).then(() => toast.success('Rejected.')); }} className="text-[10px] px-2 py-1 bg-danger/10 text-danger-text rounded hover:bg-danger/10">Reject</button>
                  </>}
                  {doc.status === 'verified' && <span className="text-[10px] text-success-text font-medium">Verified ✓</span>}
                  {doc.status === 'not_applicable' && <span className="text-[10px] text-gray-400 italic">N/A</span>}
                  {doc.status === 'pending' && <span className="text-[10px] text-warning-text">Awaiting</span>}
                </div>
              </div>
            ))}
          </div>
        </Section>
        </div>

        {/* Section: Employee Account — provision + activate the real Zynx login */}
        <div className="lg:col-span-2">
        <Section title="Employee Account">
          {!c.employeeUserId ? (
            <div className="flex items-center justify-between gap-3 p-2">
              <p className="text-xs text-muted-foreground">No Zynx account has been created for this candidate yet.</p>
              <button
                onClick={() => convertMut.mutateAsync(c._id).then((r: any) => toast.success(r?.account ? `Account created: ${r.account.username} (inactive)` : 'Converted to employee.'))}
                disabled={convertMut.isPending}
                className="text-[10px] px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 whitespace-nowrap"
              >
                {convertMut.isPending ? 'Creating...' : 'Convert to Employee'}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 p-2">
              <div className="text-xs">
                <span className="font-medium">{c.employeeUserId.username}</span>
                {c.employeeUserId.active ? (
                  <span className="ml-2 text-[10px] text-success-text font-medium">Active ✓</span>
                ) : (
                  <span className="ml-2 text-[10px] text-warning-text font-medium">Inactive — cannot log in yet</span>
                )}
              </div>
              {!c.employeeUserId.active && (
                <button
                  onClick={() => activateMut.mutateAsync(c._id).then((r: any) => {
                    toast.success('Account activated.');
                    if (r?.emailDraft) setEmailDraft(r.emailDraft);
                  })}
                  disabled={activateMut.isPending}
                  className="text-[10px] px-2 py-1 bg-success/10 text-success-text rounded hover:bg-success/10 flex items-center gap-1 whitespace-nowrap"
                >
                  <KeyRound className="h-3 w-3" />{activateMut.isPending ? 'Activating...' : 'Activate Account'}
                </button>
              )}
            </div>
          )}
        </Section>
        </div>

        {/* Section: Onboarding Meta */}
        <Section title="Onboarding Info">
          <Grid>
            <KV label="Status" value={c.onboardingStatus?.replace(/_/g, ' ')} />
            <KV label="Completion" value={`${c.completionPercent}%`} />
            <KV label="Added By" value={c.addedBy?.display_name || '-'} />
            <KV label="Added Time" value={c.addedAt ? new Date(c.addedAt).toLocaleString('en-IN') : '-'} />
            <KV label="Modified By" value={c.modifiedBy?.display_name || '-'} />
            <KV label="Modified Time" value={c.modifiedAt ? new Date(c.modifiedAt).toLocaleString('en-IN') : '-'} />
            <KV label="E-Joining Link" value={c.eJoiningToken ? `/e-joining/${c.eJoiningToken}` : '-'} span link />
          </Grid>
        </Section>
      </div>
      </div>
      </div>

      {/* Activation email draft — HR reviews/sends the temp password, same pattern as offers/interviews */}
      {emailDraft && (
        <EmailSendModal
          open={!!emailDraft}
          onClose={() => setEmailDraft(null)}
          initialTo={emailDraft.to}
          initialSubject={emailDraft.subject}
          initialHtml={emailDraft.html}
          onSent={() => setEmailDraft(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD DIRECT HIRE MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function AddDirectHireModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const addMut = useAddDirectHire();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', designation: '', department: '', dateOfJoining: '', location: '', isFresher: false });
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.firstName || !form.lastName) { toast.error('First and last name required.'); return; }
    try {
      const result = await addMut.mutateAsync(form);
      toast.success(`Direct hire added. E-Joining link: ${result.eJoiningLink}`);
      onClose();
      setForm({ firstName: '', lastName: '', email: '', phone: '', designation: '', department: '', dateOfJoining: '', location: '', isFresher: false });
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Failed.'); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Direct Hire" size="lg" footer={
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={handleCreate} disabled={addMut.isPending}>{addMut.isPending ? 'Adding...' : 'Add & Generate E-Joining Link'}</Button></div>
    }>
      <div className="space-y-3 py-3">
        <p className="text-sm text-muted-foreground">Add a candidate who was hired outside the recruitment pipeline (referral, direct hire). An E-Joining link will be generated for them to fill their details.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input name="dh-fn" label="First Name *" value={form.firstName} onChange={e => set('firstName', e.target.value)} />
          <Input name="dh-ln" label="Last Name *" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
          <Input name="dh-email" label="Email" value={form.email} onChange={e => set('email', e.target.value)} />
          <Input name="dh-phone" label="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
          <Input name="dh-desg" label="Designation" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="e.g. BDE" />
          <Input name="dh-dept" label="Department" value={form.department} onChange={e => set('department', e.target.value)} placeholder="e.g. Sales" />
          <Input name="dh-doj" label="Date of Joining" type="date" value={form.dateOfJoining} onChange={e => set('dateOfJoining', e.target.value)} />
          <Input name="dh-loc" label="Location" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Pune" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.isFresher} onChange={e => set('isFresher', e.target.checked)} className="rounded" />
          <label className="text-sm">Candidate is a fresher (no previous experience docs needed)</label>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 pb-1 border-b">{title}</h3>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">{children}</div>;
}

function KV({ label, value, span, link }: { label: string; value?: string; span?: boolean; link?: boolean }) {
  return (
    <div className={span ? 'col-span-2' : ''}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      {link && value && value !== '-' ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="block text-xs font-medium text-primary hover:underline flex items-center gap-1">{value}<ExternalLink className="h-3 w-3" /></a>
      ) : (
        <p className="text-xs font-medium capitalize">{value || '-'}</p>
      )}
    </div>
  );
}

function fmtAddr(addr?: { street?: string; city?: string; state?: string; pincode?: string }) {
  if (!addr) return '-';
  return [addr.street, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-';
}
